import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { InMemoryCache } from '@apollo/client';
import { relayStylePagination } from '@apollo/client/utilities';
import * as Eq from 'fp-ts/Eq';
import { constFalse, identity, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as RA from 'fp-ts/ReadonlyArray';
import combinedPossibleTypes from './combined-possible-types.json';
var referenceEq = pipe(Eq.eqStrict, Eq.contramap(function (x) {
  return x.__ref;
}));

var makeEmptyData = function makeEmptyData() {
  return {
    edges: [],
    pageInfo: {
      hasPreviousPage: false,
      hasNextPage: true,
      startCursor: '',
      endCursor: ''
    }
  };
};

var ConnectionFieldPolicy = identity;

var hasClientField = function hasClientField(fieldNode, field) {
  var _fieldNode$selectionS;

  return pipe(fieldNode === null || fieldNode === void 0 ? void 0 : (_fieldNode$selectionS = fieldNode.selectionSet) === null || _fieldNode$selectionS === void 0 ? void 0 : _fieldNode$selectionS.selections, O.fromNullable, O.match(constFalse, RA.exists(function (selection) {
    return selection.kind === 'Field' && selection.name.value === field;
  })));
};

export var constructCache = function constructCache() {
  return new InMemoryCache({
    possibleTypes: combinedPossibleTypes,
    typePolicies: {
      Query: {
        fields: {
          notifications: ConnectionFieldPolicy({
            keyArgs: false,
            merge: function merge(existing, incoming, _ref) {
              var _existing$edges, _incoming$edges;

              var variables = _ref.variables;
              return variables !== null && variables !== void 0 && variables.override ? incoming : _objectSpread(_objectSpread({}, incoming), {}, {
                edges: ((_existing$edges = existing === null || existing === void 0 ? void 0 : existing.edges) !== null && _existing$edges !== void 0 ? _existing$edges : []).concat((_incoming$edges = incoming === null || incoming === void 0 ? void 0 : incoming.edges) !== null && _incoming$edges !== void 0 ? _incoming$edges : [])
              });
            }
          }),
          feedItems: ConnectionFieldPolicy({
            keyArgs: false,
            merge: function merge(existing, incoming, _ref2) {
              var _existing$edges2, _incoming$edges2;

              var args = _ref2.args;
              return args !== null && args !== void 0 && args.before ? _objectSpread(_objectSpread({}, incoming), {}, {
                edges: ((_existing$edges2 = existing === null || existing === void 0 ? void 0 : existing.edges) !== null && _existing$edges2 !== void 0 ? _existing$edges2 : []).concat((_incoming$edges2 = incoming === null || incoming === void 0 ? void 0 : incoming.edges) !== null && _incoming$edges2 !== void 0 ? _incoming$edges2 : [])
              }) : incoming;
            }
          }),
          rewards: ConnectionFieldPolicy({
            keyArgs: ['sortType', 'sufficientPoints', 'categoryIds', 'onWishlist'],
            merge: function merge(existing, incoming, _ref3) {
              var _existing$edges3, _incoming$edges3;

              var args = _ref3.args;
              return args !== null && args !== void 0 && args.after ? _objectSpread(_objectSpread({}, incoming), {}, {
                edges: ((_existing$edges3 = existing === null || existing === void 0 ? void 0 : existing.edges) !== null && _existing$edges3 !== void 0 ? _existing$edges3 : []).concat((_incoming$edges3 = incoming === null || incoming === void 0 ? void 0 : incoming.edges) !== null && _incoming$edges3 !== void 0 ? _incoming$edges3 : [])
              }) : incoming;
            }
          }),
          challenges: relayStylePagination(['channelId', 'categoryId', 'state']),
          channels: relayStylePagination(function (_args, _ref4) {
            var field = _ref4.field;
            return hasClientField(field, 'includeFirstAsKeyArg') ? ['categoryIds', 'first'] : ['categoryIds'];
          }),
          availableChallenges: relayStylePagination(function (_args, _ref5) {
            var field = _ref5.field;
            var baseChallengeKeyArgs = ['channelId', 'categoryIds', 'sortType', 'inProgressOnly', 'includeChannelContent'];
            return hasClientField(field, 'includeFirstAsKeyArg') ? [].concat(baseChallengeKeyArgs, ['first']) : baseChallengeKeyArgs;
          }),
          completedChallenges: relayStylePagination(['channelId', 'categoryIds']),
          pendingApprovalChallenges: relayStylePagination(['channelId', 'categoryIds']),
          savedForLaterChallenges: relayStylePagination(['channelId', 'categoryIds', 'inProgressOnly']),
          unlockedChallenges: identity({
            keyArgs: ['completedChallengeId'],
            merge: function merge(existing, incoming) {
              return RA.union(referenceEq)(incoming !== null && incoming !== void 0 ? incoming : [])(existing !== null && existing !== void 0 ? existing : []);
            }
          })
        }
      },
      Channel: {
        fields: {
          challenges: relayStylePagination(['channelId', 'categoryId', 'state'])
        }
      },
      PublicResponsesInterface: {
        fields: {
          publicResponses: relayStylePagination(['id'])
        }
      },
      PublicResponse: {
        fields: {
          comments: relayStylePagination(['id']),
          likes: relayStylePagination(['id'])
        }
      },
      PublicResponseComment: {
        fields: {
          likes: relayStylePagination(['id'])
        }
      },
      Member: {
        fields: {
          recentActivity: ConnectionFieldPolicy({
            keyArgs: false,
            merge: function merge(existing, incoming, _ref6) {
              var _existing$edges4, _existing, _incoming$edges4;

              var args = _ref6.args;

              if (!existing) {
                existing = makeEmptyData();
              }

              if (!incoming) {
                return existing;
              }

              return args !== null && args !== void 0 && args.before ? _objectSpread(_objectSpread({}, incoming), {}, {
                edges: ((_existing$edges4 = (_existing = existing) === null || _existing === void 0 ? void 0 : _existing.edges) !== null && _existing$edges4 !== void 0 ? _existing$edges4 : []).concat((_incoming$edges4 = incoming === null || incoming === void 0 ? void 0 : incoming.edges) !== null && _incoming$edges4 !== void 0 ? _incoming$edges4 : [])
              }) : incoming;
            }
          })
        }
      }
    }
  });
};